import { Box, Image, Text } from '@everlywell/ui-kit';
import { SectionHeader } from 'components/SectionHeader';
import { Link } from 'gatsby';
import React, { useState, FC } from 'react';
import {
  Card,
  ProductCardProps,
  ProductCardGridProps,
} from 'utils/types/index';

const ProductCard: FC<ProductCardProps> = ({
  title,
  icon,
  description,
  link,
}) => {
  const content = (
    <div data-testid="product-card">
      <Box
        width="100%"
        minHeight={['250px', '400px']}
        p={['1rem', '1.5rem']}
        bg="white"
        borderRadius="0.75rem"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        mx="auto"
        boxShadow="none"
        transition="box-shadow 0.28s ease-in-out"
        _hover={{
          boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
        }}
      >
        {icon?.url ? (
          <Image
            src={icon.url}
            alt={title}
            width={['75px', '88px']}
            height={['75px', '88px']}
          />
        ) : (
          <Box
            bg="#f0f0f0"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {icon?.url ? `No icon: ${icon.url}` : 'No icon'}
          </Box>
        )}
        <Box>
          <Box
            data-testid="product-title"
            color="#0f0f0f"
            fontFamily="heading"
            fontSize={['22px', '32px']}
            fontWeight="400"
            textAlign="left"
            lineHeight={['1.5rem', '2rem']}
            pb={description ? ['0.2rem', '15px'] : ['0.5rem', '2rem']}
          >
            {title}
          </Box>
          {description && (
            <Box>
              <Text size="16px" color="#545454">
                {description}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );

  return link ? <Link to={link}>{content}</Link> : content;
};

export default ProductCard;

export const ProductCardGrid: FC<ProductCardGridProps> = ({
  backgroundColor,
  cards,
  sectionHeader,
  showOverlay = false,
  cardsPerRow = 4,
}) => {
  const [showAll, setShowAll] = useState<boolean>(false);

  const initialCards: Card[] = cards.slice(0, 8);
  const initialMobileCards: Card[] = cards.slice(0, 4);
  const displayedCards: Card[] = showAll ? cards : initialCards;
  const displayedMobileCards: Card[] = showAll ? cards : initialMobileCards;

  const toggleShowAll = (): void => {
    setShowAll(!showAll);
  };

  return (
    <Box
      as="section"
      backgroundColor={backgroundColor || '#f9f9f6'}
      padding={['3rem 4px', '6rem 4px']}
      pb={showOverlay ? '0' : '4rem'}
    >
      <Box maxWidth="1440px" mx="auto" width="100%">
        {sectionHeader && (
          <SectionHeader
            eyebrow={sectionHeader.eyebrow}
            title={sectionHeader.title ?? ''}
            description={
              sectionHeader.description?.childMarkdownRemark?.rawMarkdownBody
            }
            titleMaxWidth="800px"
          />
        )}

        {/* Desktop Grid */}
        <Box position="relative" mx="auto" width="100%">
          <Box
            display={['none', 'grid', 'grid']}
            gridTemplateColumns={`repeat(${cardsPerRow}, 1fr)`}
            gap="0.75rem"
            my="4rem"
            pb="0"
            px="1rem"
          >
            {displayedCards.map((card, index) => (
              <ProductCard
                key={index}
                title={card?.title || ''}
                link={card?.link || ''}
                icon={{ url: card.icon?.url || '' }}
                description={
                  typeof card.description === 'string'
                    ? card.description
                    : card.description?.childMarkdownRemark?.rawMarkdownBody ||
                      ''
                }
              />
            ))}
          </Box>

          {showOverlay && !showAll && (
            <Box
              position="absolute"
              bottom="0"
              left="0"
              width="100%"
              height="300px"
              background="linear-gradient(180deg, rgba(249, 249, 246, 0) 0%, #f9f9f6 70%)"
              display={['none', 'block', 'block']}
              pointerEvents="none"
            />
          )}

          {showOverlay && (
            <Box
              as="button"
              position="absolute"
              bottom={showAll ? '-4rem' : '0'}
              left="50%"
              transform="translateX(-50%)"
              bg="white"
              color="#0f0f0f"
              fontWeight="500"
              px="3rem"
              py="0.75rem"
              borderRadius="full"
              display={['none', 'block', 'block']}
              zIndex={2}
              onClick={toggleShowAll}
            >
              {showOverlay && showAll ? 'View less' : 'View all'}
            </Box>
          )}
        </Box>

        {/* Mobile Grid */}
        <Box
          display={['block', 'none', 'none']}
          position="relative"
          mb={showOverlay && showAll ? '0' : '-4rem'}
        >
          <Box
            display={['grid', 'none', 'none']}
            gridTemplateColumns="repeat(2, 1fr)"
            gap="0.5rem"
            my="2rem"
            pb="0"
          >
            {displayedMobileCards.map((card, index) => (
              <ProductCard
                key={index}
                title={card?.title || ''}
                icon={{ url: card?.icon?.url || '' }}
                description={
                  typeof card.description === 'string'
                    ? card.description
                    : card.description?.childMarkdownRemark?.rawMarkdownBody ||
                      ''
                }
              />
            ))}
          </Box>

          {showOverlay && !showAll && (
            <Box
              position="absolute"
              bottom="0"
              left="0"
              width="100%"
              height="300px"
              background="linear-gradient(to bottom, rgba(249, 249, 246, 0) 0%, #f9f9f6 70%)"
              pointerEvents="none"
            />
          )}

          {showOverlay && !showAll && (
            <Box
              as="button"
              position="absolute"
              bottom={showOverlay && showAll ? '-3rem' : '0rem'}
              left="50%"
              transform="translateX(-50%)"
              bg="white"
              color="#0f0f0f"
              fontWeight="500"
              px="3rem"
              py="0.75rem"
              borderRadius="full"
              zIndex={2}
              onClick={toggleShowAll}
            >
              {showOverlay && showAll ? 'View less' : 'View all'}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
